import { Injectable } from '@angular/core';
import { LocalStorageKeys } from '@config/app.config';
import { BehaviorSubject } from 'rxjs';
import { IDepartment } from '@core/models/department.model';
import { LocalStoreService } from './localstore.service';

@Injectable({
  providedIn: 'root',
})
export class DepartmentContextService {
  private readonly departmentSubject = new BehaviorSubject<string[]>(
    this.localService.get(LocalStorageKeys.USER_DEPARTMENTS) || [],
  );

  private readonly activeDepartmentSubject = new BehaviorSubject<string | null>(
    this.localService.get(LocalStorageKeys.ACTIVE_DEPARTMENT),
  );

  // hold the raw department objects returned from the login endpoint
  private readonly departmentDetailsSubject = new BehaviorSubject<
    IDepartment[]
  >(this.localService.get(LocalStorageKeys.USER_DEPARTMENT_DETAILS) || []);

  /** Observable for components that need the full department data */
  departmentDetails$ = this.departmentDetailsSubject.asObservable();

  department$ = this.departmentSubject.asObservable();
  activeDepartment$ = this.activeDepartmentSubject.asObservable();

  constructor(private readonly localService: LocalStoreService) {}

  setDepartment(departments: string[]): void {
    this.departmentSubject.next(departments);
    this.localService.store(LocalStorageKeys.USER_DEPARTMENTS, departments);
    if (departments.length === 1) {
      this.setActiveDepartment(departments[0]);
    }
  }

  /**
   * Save a list of department DTOs from the login call so that UI
   * components (like department picker) can render human-readable names.
   */
  setDepartmentDetails(details: IDepartment[]): void {
    this.departmentDetailsSubject.next(details);
    this.localService.store(LocalStorageKeys.USER_DEPARTMENT_DETAILS, details);
  }

  /**
   * Synchronous getter for components that cannot subscribe to observable.
   */
  getDepartmentDetails(): IDepartment[] {
    return this.departmentDetailsSubject.value;
  }
/* Set the active department and persist it in local storage. */
  setActiveDepartment(department: string): void {
    this.localService.store(LocalStorageKeys.ACTIVE_DEPARTMENT, department);
    this.activeDepartmentSubject.next(department);
  }
/* Get the currently active department. */
  getActiveDepartment(): string | null {
    return this.activeDepartmentSubject.value;
  }
/* Get the list of department internal names. */
  getDepartments(): string[] {
    return this.departmentSubject.value;
  }
/* Get the ID of the currently active department by matching the internal name with the department details. */
  getActiveDepartmentId(): string | null {
    const activeDept = this.activeDepartmentSubject.value;
    const departments = this.departmentDetailsSubject.value;

    const dept = departments.find((d) => d.internalName === activeDept);

    return dept ? dept.id : null;
  }
/* Reset the department context, clearing all stored values. */
  reset(): void {
    this.departmentSubject.next([]);
    this.activeDepartmentSubject.next(null);
  }
}
